import { T } from "@classdojo/web";
import ToastBanner from "@classdojo/web/nessie/components/ToastBanner";
import { RAW_cssValue, ThemeUIStyleObject } from "@classdojo/web/nessie/stylingLib";
import { translate } from "@classdojo/web/utils/translate";
import React from "react";
import { toast } from "react-toastify";
import { Group } from "src/pods/groups";
import { Student } from "src/pods/students";
import logEvent from "src/utils/logEvent";

type GroupsDisplayProps = {
  groups?: Group[];
  baseAvatarURL?: string | null;
  isReceiver?: boolean;
};
// eslint-disable-next-line react-prefer-function-component/react-prefer-function-component
export default class GroupsDisplay extends React.Component<GroupsDisplayProps> {
  componentDidMount() {
    logEvent({
      eventName: "groupcreator.result.open",
    });
    if (this.props.groups?.length) {
      toast(
        <ToastBanner
          text={translate({
            str: "dojo.toolkit:group_maker.created_successfully",
            fallback: "Groups created successfully",
          })}
        />,
      );
    }
  }

  componentWillUnmount() {
    logEvent({
      eventName: "groupcreator.result.close",
    });
  }

  render() {
    const { groups, baseAvatarURL, isReceiver } = this.props;

    function renderStudent(student: Student) {
      return (
        <div key={student._id} sx={groupMakerDisplayStudentStyle}>
          <img sx={groupMakerDisplayAvatarStyle} src={`${baseAvatarURL || ""}${student.avatar}`} role="presentation" />
          <p sx={isReceiver ? groupMakerDisplayNameReceiverStyle : groupMakerDisplayNameStyle}>
            <T
              str="dojo.toolkit:student_name_with_last_initial"
              subs={{
                firstName: student.firstName,
                lastName: student.lastName,
                lastInitial: student.lastName.slice(0, 1),
              }}
              fallback="__firstName__ __lastInitial__."
            />
          </p>
        </div>
      );
    }

    function renderGroup(group: Group, idx: number) {
      return (
        <section key={idx} sx={groupMakerDisplayCardStyle} role="status" aria-live="polite">
          <p sx={{ fontSize: "16px", fontWeight: "bold" }}>
            {translate({
              str: "dojo.toolkit:group_maker.group_name",
              fallback: "Group __number__",
              subs: { number: idx + 1 },
            })}
          </p>
          {group.students.map(renderStudent)}
        </section>
      );
    }

    return <div sx={groupMakerDisplayGroupsStyle}>{groups && groups.map(renderGroup)}</div>;
  }
}

const groupMakerDisplayGroupsStyle: ThemeUIStyleObject = {
  display: "flex",
  flexWrap: "wrap",
  alignItems: "flex-start",
  justifyContent: "flex-start",
  margin: RAW_cssValue("0 auto 12px auto"),
};

const groupMakerDisplayCardStyle: ThemeUIStyleObject = {
  margin: RAW_cssValue("6px 12px"),
  padding: "0",
  border: "dt_card",
  borderRadius: "dt_radius_s",
  textAlign: "center",
  backgroundColor: "dt_background_primary",
  color: "dt_content_primary",
  flex: "1 1 auto",
};

const groupMakerDisplayStudentStyle: ThemeUIStyleObject = {
  height: "32px",
  margin: RAW_cssValue("15px 12px"),
  textAlign: "left",
  display: "flex",
  alignItems: "center",
};

const groupMakerDisplayNameStyle: ThemeUIStyleObject = {
  display: "inline-block",
  width: "150px",
  margin: "0",
  padding: "0",
  fontSize: "16px",
  fontWeight: "normal",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  verticalAlign: "top",
};

const groupMakerDisplayNameReceiverStyle: ThemeUIStyleObject = {
  /* receiver has bigger and bolder text */
  fontSize: "20px",
  fontSeight: "600",
};

const groupMakerDisplayAvatarStyle: ThemeUIStyleObject = {
  display: "inline-block",
  maxWidth: "32px",
  maxHeight: "32px",
  margin: RAW_cssValue("0 10px 0 0"),
  verticalAlign: "sub",
};
